// src/context/AuthContext.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { showSuccess, showError } from "../utils/toastUtils";

const AuthContext = React.createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);

  // Helpers
  const getToken = () => localStorage.getItem("token");
  const setAxiosAuthHeader = (token) => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  };

  // ✅ Login
  const login = async (email, password) => {
    setLoadingAuth(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
      });

      const { token, email: resEmail, role, adminLevel } = res.data || {};
      if (!token) throw new Error("Kein Token erhalten");

      localStorage.setItem("token", token);
      setAxiosAuthHeader(token);
      setUser({ email: resEmail, role, adminLevel, token });

      showSuccess("Erfolg!", "Login erfolgreich.");
    } catch (err) {
      if (import.meta?.env?.DEV) console.error("Login error:", err);
      showError("Fehler!", "Login fehlgeschlagen.");
    } finally {
      setLoadingAuth(false);
    }
  };

  // ✅ Registration
  const register = async (email, password) => {
    setLoadingAuth(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, {
        email,
        password,
      });

      showSuccess("Erfolg!", "Bitte bestätige deine E-Mail-Adresse.");
      return true;
    } catch (err) {
      if (import.meta?.env?.DEV) console.error("Registration error:", err);
      showError("Fehler!", "Registrierung fehlgeschlagen.");
      return false;
    } finally {
      setLoadingAuth(false);
    }
  };

  // ✅ Request Access (non-envaris domain)
const requestAccess = async (email, password) => {
  setLoadingAuth(true);
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/auth/request-access`, {
      email,
      password,
    });
    showSuccess("Anfrage gesendet!", "Deine Anfrage wurde an den Admin weitergeleitet.");
    return true; // 👈 missing
  } catch (err) {
    if (import.meta?.env?.DEV) console.error("Request access error:", err);
    showError("Fehler!", "Anfrage fehlgeschlagen.");
    return false; // 👈 missing
  } finally {
    setLoadingAuth(false);
  }
};

  // ✅ Logout
  const logout = () => {
    localStorage.removeItem("token");
    setAxiosAuthHeader(null);
    setUser(null);
    showSuccess("Abgemeldet", "Du wurdest erfolgreich ausgeloggt.");
  };

  // ✅ Restore session on reload (set axios header if token exists)
useEffect(() => {
  const token = getToken();
  if (!token) return;

  setLoadingAuth(true);
  setAxiosAuthHeader(token);

  axios
    .get(`${process.env.REACT_APP_API_URL}/auth/me`)
    .then((res) => setUser({ ...res.data, token }))
    .catch(() => logout())
    .finally(() => setLoadingAuth(false));
}, []);

  const value = useMemo(
    () => ({
      user,
      loadingAuth,
      login,
      register,
      requestAccess,
      logout,
    }),
    [user, loadingAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;